import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import featureService from '../../../core/services/featureService';
import { getLogoPath, BRAND_QUERY } from '../../utils/assetPaths';
import BrandProtectedImg from '../custom/BrandProtectedImg';
import { moduleStructure } from '../../../core/config/moduleStructure';
import { useSetup } from '../../../core/contexts/SetupContext';
import { secureGetItem } from '../../utils/secureStorage';
import { getModuleLabel, getSubsectionLabel } from '../../utils/moduleNavI18n';
import {
  shouldShowMainGammeSectionHeader,
  shouldShowMainGammeSeparator,
  shouldShowIndustrySectorSeparator,
} from '../../../core/config/moduleLicenseTiers';

import { 
  LayoutDashboard,
  FilePlus, 
  Archive, 
  Users, 
  UserCheck,
  UserCog,
  Building2,
  Truck,
  Package, 
  FolderOpen,
  Settings,
  LogOut,
  X,
  Receipt,
  BarChart3,
  Wallet,
  DollarSign,
  LineChart,
  Boxes,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Calculator,
  Clock,
  DollarSign as Salary,
  Vault,
  Landmark,
  Factory,
  Cog,
  Wrench,
  Layers,
  FileText,
  UserPlus,
  Monitor,
  ShoppingCart,
  BarChart3 as AnalyticsIcon,
  ChartBar as ChartBarIcon,
  FileSpreadsheet,
  ClipboardList,
  Calendar,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  ArrowRight,
  BookOpen,
  Scale,
  Bell,
  Target,
  Mail,
  TrendingUp,
  Megaphone,
  MessageSquare,
  Layout,
  GitBranch,
  CheckSquare,
  Flag,
  Copy,
  PenTool,
  Edit,
  Droplet,
  Shield,
  ShieldCheck,
  MapPin,
  Store,
  Route,
  Database,
  Car,
  Link2,
  PieChart,
  ClipboardCheck,
  XCircle,
  Banknote,
  FileCheck,
  Globe,
  Building,
  AlertCircle,
  ArrowDownToLine,
  ArrowUpFromLine,
  CreditCard,
  Search
} from 'lucide-react';
import { GlobeAltIcon } from '@heroicons/react/24/outline';

function SidebarTierSectionHeader({ show, label, isLightTheme, collapsed, showLine = true }) {
  if (!show || collapsed) return null;

  return (
    <div className="mb-1 shrink-0" role="presentation">
      {showLine && (
        <div
          className={`mx-3 mt-3 h-px shrink-0 ${
            isLightTheme ? 'bg-gray-300' : 'bg-white/20'
          }`}
          role="separator"
          aria-hidden="true"
        />
      )}
      <p
        className={`px-3 pb-0.5 text-[10px] font-semibold uppercase tracking-wider ${
          showLine ? 'pt-2' : 'pt-1'
        } ${isLightTheme ? 'text-gray-400' : 'text-white/35'}`}
      >
        {label}
      </p>
    </div>
  );
}

const Sidebar = ({ sidebarOpen, sidebarCollapsed = false, toggleSidebar, toggleSidebarCollapsed, isMobile = false, isLightTheme = false }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useSetup();
  const [expandedModules, setExpandedModules] = useState({});
  const [user, setUser] = useState({ username: t('common.user'), role: t('sidebar.user_role') });
  
  // Scroll navigation for collapsed sidebar
  const navScrollRef = useRef(null);
  const [showUpArrow, setShowUpArrow] = useState(false);
  const [showDownArrow, setShowDownArrow] = useState(false);
  
  // Refs for module buttons (for dropdown positioning in collapsed mode)
  const moduleButtonRefs = useRef({});
  
  // Force dropdown re-positioning on scroll
  const [dropdownPositionTrigger, setDropdownPositionTrigger] = useState(0);
  const [, setLicenseRevision] = useState(0);

  useEffect(() => {
    const onLicenseSynced = () => {
      featureService.clearCache();
      setLicenseRevision((n) => n + 1);
    };
    window.addEventListener('qawaem-license-synced', onLicenseSynced);
    return () => window.removeEventListener('qawaem-license-synced', onLicenseSynced);
  }, []);
  
  // Ref for dropdown to detect clicks outside
  const dropdownRef = useRef(null);
  
  // 🎯 CRITICAL: Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarCollapsed && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Check if click is on a module button
        const clickedButton = Object.values(moduleButtonRefs.current).find(btn => 
          btn && btn.contains(event.target)
        );
        
        // Close dropdown only if not clicking a module button
        if (!clickedButton) {
          setExpandedModules({});
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarCollapsed]);
  
  // Load user from localStorage (set by authService after login)
  useEffect(() => {
    try {
      const storedUser = secureGetItem('qawaem_user');
      console.log('📦 Sidebar loading user from secure storage:', storedUser);
      if (storedUser) {
        console.log('✅ Decrypted user data:', storedUser);
        setUser({
          username: storedUser.username || t('common.user'),
          role: storedUser.role || t('sidebar.user_role')
        });
      } else {
        console.warn('⚠️ No user data found in secure storage');
      }
    } catch (error) {
      console.error('❌ Error loading user from secure storage:', error);
    }
  }, [t]);
  
  // Toggle module expansion
  const toggleModule = (moduleKey) => {
    // Find the module
    const module = moduleStructure.find(m => m.key === moduleKey);
    
    // 🎯 FIX: Only navigate if module is ALREADY expanded (clicking again)
    // Otherwise, just open the dropdown
    if (expandedModules[moduleKey]) {
      // Module is already open, clicking again navigates to dashboard
      if (module && module.subsections && module.subsections.length > 0) {
        const firstSubsection = module.subsections[0];
        navigate(firstSubsection.href);
      }
    }
    
    // 🎯 ACCORDION BEHAVIOR: Close all others when opening one (applies to both modes)
    setExpandedModules(prev => {
      const newState = {};
      // If this module is already open, close it. Otherwise, open only this one
      newState[moduleKey] = !prev[moduleKey];
      return newState;
    });
  };

  // Check scroll position for collapsed sidebar
  const checkScroll = () => {
    if (navScrollRef.current && sidebarCollapsed) {
      const { scrollTop, scrollHeight, clientHeight } = navScrollRef.current;
      setShowUpArrow(scrollTop > 10);
      setShowDownArrow(scrollTop < scrollHeight - clientHeight - 10);
      // 🎯 Trigger dropdown re-positioning when scrolling!
      setDropdownPositionTrigger(prev => prev + 1);
    }
  };

  // Scroll navigation up
  const scrollUp = () => {
    if (navScrollRef.current) {
      navScrollRef.current.scrollBy({ top: -200, behavior: 'smooth' });
    }
  };

  // Scroll navigation down
  const scrollDown = () => {
    if (navScrollRef.current) {
      navScrollRef.current.scrollBy({ top: 200, behavior: 'smooth' });
    }
  };

  // Filter modules based on access (keep moduleStructure order — no gamme sort)
  const filteredModules = moduleStructure
    .filter(module => featureService.hasModuleAccess(module.key))
    .map(module => ({
      ...module,
      subsections: module.subsections
    }))
    .filter(module => module.subsections.length > 0);

  const filteredModuleKeys = filteredModules.map((module) => module.key);

  /** One licensed/selected module: show its pages as a flat list (no module dropdown row). */
  const isSingleModuleNav = filteredModules.length === 1;

  const licensedModuleKeys = filteredModules.map((m) => m.key).join('|');

  // Expand the licensed module for the current route, or the first licensed module on first load.
  useEffect(() => {
    if (filteredModules.length <= 1) {
      setExpandedModules({});
      return;
    }

    const currentModule = featureService.getLicensedModuleForPath(location.pathname);
    const isGenericHome = location.pathname === '/dashboard';

    if (!currentModule && isGenericHome && filteredModules.length > 0) {
      const firstHref = filteredModules[0].subsections[0]?.href;
      if (firstHref && location.pathname !== firstHref) {
        navigate(firstHref, { replace: true });
        return;
      }
    }

    const moduleToExpand = currentModule || filteredModules[0];
    if (moduleToExpand) {
      setExpandedModules({ [moduleToExpand.key]: true });
    }
  }, [location.pathname, licensedModuleKeys, navigate, filteredModules.length]);
  
  const isRTL = i18n.language && i18n.language.startsWith('ar');
  const chevronExpandClass = isExpanded =>
    isExpanded ? (isRTL ? '-rotate-90' : 'rotate-90') : '';
  
  const handleLogout = () => {
    if (logout) {
      logout();
    }
  };
  
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    
    // Save language preference
    try {
      const settings = JSON.parse(localStorage.getItem('qawaem-settings') || '{}');
      settings.invoicing = settings.invoicing || {};
      settings.invoicing.language = lng;
      localStorage.setItem('qawaem-settings', JSON.stringify(settings));
      localStorage.setItem('language', lng);
      
      // Update document direction
      if (lng === 'ar') {
        document.documentElement.dir = 'rtl';
        document.documentElement.lang = 'ar';
      } else {
        document.documentElement.dir = 'ltr';
        document.documentElement.lang = lng;
      }
      
      // Force page reload to apply RTL changes properly
      setTimeout(() => window.location.reload(), 100);
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  };

  // Check scroll on mount and when collapsed state changes
  useEffect(() => {
    if (sidebarCollapsed) {
      checkScroll();
      const navElement = navScrollRef.current;
      if (navElement) {
        navElement.addEventListener('scroll', checkScroll);
        return () => navElement.removeEventListener('scroll', checkScroll);
      }
    }
  }, [sidebarCollapsed, filteredModules.length]);

  // 📦 MODULE STRUCTURE IMPORTED FROM SHARED CONFIG
  // Now both Sidebar and TopBar use the same source of truth!
  
  const menuItems = [];
  
  // 🔑 FEATURE MAPPING: Map menu item hrefs to feature routes from license config
  const featureMap = {
    '/overview': '/dashboard',
    '/production-dashboard': '/bord-fabrication',
    '/dashboard': '/bord-facturation',
    '/document-creation': '/documents/new',
    '/archives': '/archives',
    '/clients': '/clients',
    '/trading-partners': '/partners',
    '/products': '/products',
    '/articles/matieres-premieres': '/articles/matieres-premieres',
    '/articles/produits-semi-finis': '/articles/produits-semi-finis',
    '/articles/produits-finis': '/articles/produits-finis',
    '/articles/equipements': '/articles/equipements',
    '/articles/pieces-detachees': '/articles/pieces-detachees',
    '/technical-data': '/technical-data',
    '/categories': '/categories',
    '/payments': '/payments',
    // '/employees-simple': '/employees', // REMOVED
    '/employees': '/employees-management',
    '/operators-management': '/operators',
    '/departments': '/departments',
    '/achats-ventes': '/achats-ventes',
    '/stock': '/stock',
    '/production-stock': '/production-stock',
    '/inventaire': '/inventaire',
    '/returns': '/retours',
    '/finances': '/finances',
    '/reports': '/reports',
    '/logistique': '/logistics',
    '/production-planning': '/production-planning',
    '/atelier-qualite': '/atelier-qualite',
    '/import-export': '/import-export',
    '/timetracking': '/time-tracking',
    '/workstation-tracking': '/workstations',
    '/salaries': '/salaries',
    '/treasury': '/treasury',
    '/banking': '/banking',
    '/comptabilite': '/comptabilite',
    '/analysis': '/analysis',
    '/advanced-analytics': '/advanced-analytics',
    '/orders': '/orders',
    '/advanced-reports': '/advanced-reports',
    '/settings': '/settings'
  };
  
  // 🔒 FILTER MENU ITEMS: Show only features enabled for current license tier
  const visibleMenuItems = menuItems.filter(item => {
    const featureRoute = featureMap[item.href];
    
    // If no mapping exists, log warning and HIDE by default (strict mode)
    if (!featureRoute) {
      console.warn(`⚠️ No feature mapping for menu item: ${item.href} - Hiding by default`);
      return false; // Hide unmapped items for security
    }
    
    // Check if feature is enabled for current license
    const isEnabled = featureService.isFeatureEnabled(featureRoute);
    
    // Debug log (can be removed in production)
    if (!isEnabled) {
      console.log(`🔒 Feature locked: ${item.href} → ${featureRoute}`);
    }
    
    return isEnabled;
  });
  
  const closeSidebar = () => {
    if (isMobile) {
      toggleSidebar(false);
    }
  };

  // Animation variants for menu items — disabled on startup to avoid sidebar ghost/overlap on large menus.
  const itemVariants = {
    hidden: { opacity: 1, x: 0 },
    visible: { opacity: 1, x: 0, transition: { duration: 0 } },
  };

  return (
    <>
      {/* Desktop sidebar */}
      <motion.aside
        initial={{ x: 0 }}
        animate={{ x: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className={`hidden lg:flex flex-col ${sidebarCollapsed ? 'w-24' : 'w-64'} h-full transition-all duration-300 ease-in-out ${isLightTheme 
          ? `bg-white/90` 
          : `bg-white/10 backdrop-blur-lg`
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header with logo and toggle button */}
          <div className="flex-shrink-0 pt-5 pb-4 overflow-hidden">
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} ${sidebarCollapsed ? 'px-0' : 'px-4'}`}>
              {sidebarCollapsed ? (
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full flex items-center justify-center px-1 mx-auto"
                >
                  <BrandProtectedImg 
                    src={`${getLogoPath(isLightTheme)}${BRAND_QUERY}`} 
                    alt={t('sidebar.software_name')} 
                    className="h-7 w-auto max-w-[44px] object-contain" 
                    onError={(e) => {
                      e.target.src = `${getLogoPath(!isLightTheme)}${BRAND_QUERY}`;
                    }}
                  />
                </motion.div>
              ) : (
                <>
                  <motion.div
                    initial={{ opacity: 1, y: 0 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0 }}
                    className="flex flex-col items-center w-full px-2"
                  >
                    {/* Full Qawaem Logo */}
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="mb-1"
                    >
                      <BrandProtectedImg 
                        src={`${getLogoPath(isLightTheme)}${BRAND_QUERY}`} 
                        alt="Qawaem ERP" 
                        className="h-6 w-auto max-w-[9.5rem] object-contain" 
                        onError={(e) => {
                          e.target.src = `${getLogoPath(!isLightTheme)}${BRAND_QUERY}`;
                        }}
                      />
                    </motion.div>
                    {/* Tagline under logo */}
                    <div className="text-center w-full">
                      <p className={`text-[11px] leading-snug whitespace-nowrap ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`}>
                        نظام تخطيط موارد المؤسسات
                      </p>
                    </div>
                  </motion.div>
                  
                  {/* Toggle button for desktop */}
                  {!isMobile && toggleSidebarCollapsed && (
                    <button
                      onClick={toggleSidebarCollapsed}
                      className={`p-2 rounded-xl transition-colors ${isLightTheme 
                        ? 'bg-gray-100 text-gray-900 hover:bg-gray-200' 
                        : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                      title={t('common.collapse_sidebar')}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                  )}
                </>
              )}
              
              {/* Toggle button for collapsed state */}
              {sidebarCollapsed && !isMobile && toggleSidebarCollapsed && (
                <button
                  onClick={toggleSidebarCollapsed}
                  className={`p-2 rounded-xl transition-colors ${isLightTheme 
                    ? 'bg-gray-100 text-gray-900 hover:bg-gray-200' 
                    : 'bg-white/10 text-white hover:bg-white/20'
                  } absolute top-5 right-2`}
                  title={t('common.expand_sidebar')}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
              
              {/* Close button for mobile */}
              {isMobile && (
                <button
                  onClick={toggleSidebar}
                  className={`p-2 rounded-xl transition-colors ${isLightTheme 
                    ? 'bg-gray-100 text-gray-900 hover:bg-gray-200' 
                    : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <X className="h-5 w-5" />
                      </button>
              )}
            </div>
        </div>

          {/* Content area (navigation + user profile) */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          
          {/* Border that starts below header */}
          <div className={`absolute top-[63px] bottom-0 ${isRTL ? 'left-0 border-l' : 'right-0 border-r'} ${isLightTheme ? 'border-gray-200/50' : 'border-white/20'} pointer-events-none`}></div>

          {/* Scroll Up Arrow for Collapsed Sidebar */}
          {sidebarCollapsed && showUpArrow && (
            <div className="flex justify-center pb-2 relative z-50">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  scrollUp();
                }}
                className="p-2 bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 rounded-full text-white shadow-lg transition-all"
              >
                <ChevronLeft className="h-5 w-5 transform rotate-90" />
              </button>
            </div>
          )}

          {/* Navigation items */}
          <nav 
            ref={navScrollRef}
            dir={isRTL ? 'rtl' : 'ltr'}
            className={`flex-1 px-2 ${sidebarCollapsed ? 'overflow-y-auto scrollbar-hide' : 'overflow-y-auto dark-scrollbar'} min-h-0 ${isRTL ? 'text-right' : ''}`}
            onScroll={sidebarCollapsed ? checkScroll : undefined}
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(59, 130, 246, 0.8) transparent', // Blue thumb, transparent track
            }}
          >
            <div className="space-y-2">
              {filteredModules
                .map((module, moduleIndex) => {
                const isExpanded = expandedModules[module.key];
                const showFlatSubsections = isSingleModuleNav && !sidebarCollapsed;
                const showSubsectionsDesktop = !sidebarCollapsed && (showFlatSubsections || isExpanded);
                const isAnySubsectionActive = module.subsections.some(sub => 
                  location.pathname === sub.href || location.pathname.startsWith(sub.href + '/')
                );
                // Mock notification count for the module - sum of all subsections
                const moduleNotificationCount = 0; // TODO: Add real notification logic
                const showMainGammeHeader = shouldShowMainGammeSectionHeader(
                  module.key,
                  filteredModuleKeys,
                );
                const showGammeSeparator = shouldShowMainGammeSeparator(
                  module.key,
                  filteredModuleKeys,
                );
                const showIndustrySectorSeparator = shouldShowIndustrySectorSeparator(
                  module.key,
                  filteredModuleKeys,
                );
              
                              return (
                  <React.Fragment key={module.key}>
                    <SidebarTierSectionHeader
                      show={showMainGammeHeader}
                      label={t('sidebar.gamme_main')}
                      isLightTheme={isLightTheme}
                      collapsed={sidebarCollapsed}
                      showLine={false}
                    />
                    <SidebarTierSectionHeader
                      show={showGammeSeparator}
                      label={t('sidebar.gamme_industry')}
                      isLightTheme={isLightTheme}
                      collapsed={sidebarCollapsed}
                    />
                    <SidebarTierSectionHeader
                      show={showIndustrySectorSeparator}
                      label={t('sidebar.gamme_sector')}
                      isLightTheme={isLightTheme}
                      collapsed={sidebarCollapsed}
                    />
                  <div className="space-y-1">
                    {/* Module Header (Collapsible) — hidden when exactly one module and sidebar is expanded */}
                    {(!isSingleModuleNav || sidebarCollapsed) && (
                          <motion.button
                      ref={(el) => (moduleButtonRefs.current[module.key] = el)}
                      custom={moduleIndex}
                      initial={false}
                      animate="visible"
                      variants={itemVariants}
                      onClick={() => toggleModule(module.key)}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      className={`w-full group flex items-center ${sidebarCollapsed ? 'justify-center px-2 py-3' : 'justify-between px-2 py-2'} text-sm font-semibold rounded-lg transition-all duration-200 ${isLightTheme
                        ? (isAnySubsectionActive
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-gray-900 hover:bg-gray-100')
                        : (isAnySubsectionActive
                          ? 'bg-white/10 text-white'
                          : 'text-white/80 hover:bg-white/5 hover:text-white')
                      }`}
                    >
                      {sidebarCollapsed ? (
                        <module.icon
                          className="flex-shrink-0 h-5 w-5"
                          aria-hidden="true"
                        />
                      ) : (
                        <>
                          <div className="flex items-center min-w-0 flex-1">
                            <module.icon
                              className="me-3 flex-shrink-0 h-5 w-5"
                              aria-hidden="true"
                            />
                            <span className="truncate">{getModuleLabel(module, t)}</span>
                            {moduleNotificationCount > 0 && (
                              <span className="ms-2 px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded-full">
                                {moduleNotificationCount}
                              </span>
                            )}
                          </div>
                          <ChevronRight
                            className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${chevronExpandClass(isExpanded)} ${isRTL ? 'scale-x-[-1]' : ''}`}
                          />
                        </>
                      )}
                    </motion.button>
                    )}

                    {/* Module Subsections (Collapsible) */}
                    {showSubsectionsDesktop && (
                      <motion.div
                        initial={false}
                        className={showFlatSubsections ? 'space-y-1' : 'ms-4 space-y-1'}
                      >
                        {module.subsections.map((subsection) => {
                          const isCurrent = location.pathname === subsection.href || 
                                          location.pathname.startsWith(subsection.href + '/');
                          // Mock notification count - you can replace this with real data
                          const notificationCount = 0; // TODO: Add real notification logic
                          const flatSingleSubsection = showFlatSubsections;
                          const subsectionLinkClass = flatSingleSubsection
                            ? `flex items-center justify-between px-2 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${isLightTheme
                              ? (isCurrent
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900')
                              : (isCurrent
                                ? 'bg-white/10 text-white'
                                : 'text-white/80 hover:bg-white/5 hover:text-white')
                            }`
                            : `flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-all duration-200 ${isLightTheme
                              ? (isCurrent
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900')
                              : (isCurrent
                                ? 'bg-white/10 text-white'
                                : 'text-white/60 hover:bg-white/5 hover:text-white')
                            }`;
                          const subsectionIconClass = flatSingleSubsection
                            ? `me-3 flex-shrink-0 h-5 w-5${isCurrent && isLightTheme ? ' text-white' : ''}`
                            : `me-3 flex-shrink-0 h-4 w-4 ${isCurrent ? (isLightTheme ? 'text-white' : 'text-blue-500') : ''}`;
                          
                          return (
                            <div key={subsection.href} className="group relative">
                              <Link
                                to={subsection.href}
                                onClick={closeSidebar}
                                className={subsectionLinkClass}
                              >
                                <div className="flex items-center flex-1 min-w-0">
                                  <subsection.icon
                                    className={subsectionIconClass}
                                    aria-hidden="true"
                                  />
                                  <span className="truncate">{getSubsectionLabel(subsection, t)}</span>
                                  {notificationCount > 0 && (
                                    <span className="ms-2 px-1.5 py-0.5 text-xs font-semibold bg-red-500 text-white rounded-full">
                                      {notificationCount}
                                    </span>
                                  )}
                                </div>
                              </Link>
                            </div>
                          );
                        })}
                      </motion.div>
                    )}

                    {/* Collapsed Sidebar - Popup Menu on Click */}
                    {sidebarCollapsed && isExpanded && (
                      <motion.div
                        ref={dropdownRef}
                        key={`${module.key}-${dropdownPositionTrigger}`}
                        initial={{ opacity: 0, scale: 0.95, x: -10 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.95, x: -10 }}
                        transition={{ duration: 0.15 }}
                        className={`fixed z-50 w-56 rounded-lg shadow-2xl border ${isRTL ? 'right-24 left-auto' : 'left-24 right-auto'} ${
                          isLightTheme
                            ? 'bg-white border-gray-200'
                            : 'bg-gradient-to-br from-slate-700 via-blue-750 to-slate-700 border-blue-400/60'
                        }`}
                        style={{
                          boxShadow: isLightTheme
                            ? undefined
                            : '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)',
                          display: 'flex',
                          // 🎯 SMART POSITIONING & HEIGHT - Stops at screen edge with scrollbar!
                          ...((() => {
                            if (!moduleButtonRefs.current[module.key]) {
                              return { 
                                top: `${moduleIndex * 48 + 180}px`,
                                maxHeight: 'calc(100vh - 40px)',
                                flexDirection: 'column'
                              };
                            }
                            
                            const buttonRect = moduleButtonRefs.current[module.key].getBoundingClientRect();
                            const buttonTop = buttonRect.top;
                            const buttonBottom = buttonRect.bottom;
                            
                            // Calculate content height (items * height + padding + header)
                            const contentHeight = module.subsections.length * 40 + 100; // Estimated
                            const needsLongScroll = contentHeight > 400; // More than 400px = long scroll
                            
                            const spaceBelow = window.innerHeight - buttonBottom - 20; // 20px margin
                            const spaceAbove = buttonTop - 20; // 20px margin
                            
                            // 🎯 FLIP LOGIC: If content is long AND opening downward would require scrolling
                            const shouldFlipForLongContent = needsLongScroll && spaceBelow < contentHeight;
                            
                            if (shouldFlipForLongContent && spaceAbove > 300) {
                              // FLIP: Title at bottom, content scrolls up (for long lists)
                              return { 
                                bottom: `${window.innerHeight - buttonBottom}px`,
                                maxHeight: `${spaceAbove}px`,
                                top: 'auto',
                                flexDirection: 'column-reverse' // 🎯 Title at bottom!
                              };
                            }
                            
                            // Normal: Title at top, content scrolls down (for short lists or top modules)
                            const normalTop = Math.max(buttonTop, 80); // 🎯 Never go above header (80px min)
                            
                            return { 
                              top: `${normalTop}px`, // Constrained to not exceed header
                              maxHeight: `${Math.min(window.innerHeight - normalTop - 20, 600)}px`, // Adjust based on actual top
                              flexDirection: 'column'
                            };
                          })())
                        }}
                      >
                        {/* Module title in flyout — hidden when only one module (redundant) */}
                        {!isSingleModuleNav && (
                        <div className={`px-4 py-3 border-b flex-shrink-0 ${
                          isLightTheme ? 'border-gray-200' : 'border-white/10'
                        }`}>
                          <div className="flex items-center min-w-0">
                            <span className={`font-semibold text-sm truncate ${
                              isLightTheme ? 'text-gray-900' : 'text-white'
                            }`}>
                              {getModuleLabel(module, t)}
                            </span>
                          </div>
                        </div>
                        )}
                        
                        {/* Subsections List - Scrollable when content overflows */}
                        <div className="py-2 px-2 dark-scrollbar flex-1 min-h-0" style={{ 
                          overflowY: 'auto',
                          scrollbarWidth: 'thin'
                        }}>
                          {module.subsections.map((subsection) => {
                            const isCurrent = location.pathname === subsection.href || 
                                            location.pathname.startsWith(subsection.href + '/');
                            const notificationCount = 0;
                            const flatFlyout = isSingleModuleNav;
                            const flyoutLinkClass = flatFlyout
                              ? `flex items-center px-2 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                                  isLightTheme
                                    ? (isCurrent
                                      ? 'bg-blue-600 text-white shadow-sm'
                                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900')
                                    : (isCurrent
                                      ? 'bg-white/10 text-white'
                                      : 'text-white/80 hover:bg-white/5 hover:text-white')
                                }`
                              : `flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                                  isLightTheme
                                    ? (isCurrent
                                      ? 'bg-blue-600 text-white shadow-sm'
                                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900')
                                    : (isCurrent
                                      ? 'bg-white/10 text-white'
                                      : 'text-white/60 hover:bg-white/5 hover:text-white')
                                }`;
                            const flyoutIconClass = flatFlyout
                              ? `me-3 flex-shrink-0 h-5 w-5${isCurrent && isLightTheme ? ' text-white' : ''}`
                              : `me-3 flex-shrink-0 h-4 w-4 ${isCurrent ? (isLightTheme ? 'text-white' : 'text-blue-500') : ''}`;
                            
                            return (
                              <Link
                                key={subsection.href}
                                to={subsection.href}
                                onClick={() => {
                                  toggleModule(module.key);
                                  closeSidebar();
                                }}
                                className={flyoutLinkClass}
                              >
                                <subsection.icon className={flyoutIconClass} />
                                <span className="truncate flex-1">{getSubsectionLabel(subsection, t)}</span>
                                {notificationCount > 0 && (
                                  <span className="ms-2 px-1.5 py-0.5 text-xs font-semibold bg-red-500 text-white rounded-full">
                                    {notificationCount}
                                  </span>
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </div>
                  </React.Fragment>
              );
            })}
            </div>
            
            {/* Paramètres Link */}
            {!sidebarCollapsed && (
              <div className="mt-1 mb-3">
                <Link
                  to="/settings"
                  onClick={closeSidebar}
                  className={`group flex items-center px-2 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${isLightTheme 
                    ? (location.pathname === '/settings'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900')
                    : (location.pathname === '/settings'
                      ? 'bg-white/10 text-white'
                      : 'text-white/80 hover:bg-white/5 hover:text-white')
                  }`}
                >
                  <Settings className="me-3 flex-shrink-0 h-5 w-5" aria-hidden="true" />
                  <span>{t('sidebar.settings')}</span>
                </Link>
              </div>
            )}
            {sidebarCollapsed && (
              <div className="mt-1">
                <Link
                  to="/settings"
                  onClick={closeSidebar}
                  className={`group flex items-center justify-center px-2 py-3 rounded-lg transition-all duration-200 relative ${isLightTheme 
                    ? (location.pathname === '/settings'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100')
                    : (location.pathname === '/settings'
                      ? 'bg-white/10 text-white'
                      : 'text-white/70 hover:bg-white/5 hover:text-white')
                  }`}
                  title={t('sidebar.settings')}
                >
                  <Settings className="h-5 w-5" aria-hidden="true" />
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 whitespace-nowrap">
                    {t('sidebar.settings')}
                    <div className="absolute top-1/2 -left-1 transform -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                  </div>
                </Link>
              </div>
            )}
          </nav>

          {/* Scroll Down Arrow for Collapsed Sidebar */}
          {sidebarCollapsed && showDownArrow && (
            <div className="flex justify-center pt-2 relative z-50">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  scrollDown();
                }}
                className="p-2 bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 rounded-full text-white shadow-lg transition-all"
              >
                <ChevronLeft className="h-5 w-5 transform -rotate-90" />
              </button>
            </div>
          )}

          {/* User profile section */}
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0 }}
            className={`flex-shrink-0 px-3 pt-3 pb-3 border-t ${isLightTheme ? 'border-gray-200' : 'border-white/10'}`}
          >
            <Link 
              to="/profile"
              className={`flex items-center px-2 py-2 mt-1 group rounded-lg hover:bg-white/5 ${sidebarCollapsed ? 'justify-center' : ''} relative`}
              title={sidebarCollapsed ? user?.username || t('common.user') : ''}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center ${sidebarCollapsed ? '' : 'mr-3'}`}
              >
                <span className="text-sm font-medium text-white">{user?.username?.charAt(0)?.toUpperCase() || 'U'}</span>
              </motion.div>
              
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${isLightTheme ? 'text-gray-900' : 'text-white'}`}>
                    {user?.username || t('common.user')}
                  </p>
                  <p className={`text-xs truncate ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`}>
                    {user?.role || t('sidebar.user_role')}
                  </p>
                </div>
              )}
              
              {!sidebarCollapsed && (
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    logout();
                  }}
                  className={`ml-auto p-1 transition-colors ${isLightTheme 
                    ? 'text-gray-600 hover:text-gray-900' 
                    : 'text-white/60 hover:text-white'
                  }`}
                  aria-label={t('sidebar.logout')}
                  title={t('sidebar.logout')}
                >
                  <LogOut className="h-4 w-4" />
                </motion.button>
              )}
              
              {/* Tooltip for collapsed user profile */}
              {sidebarCollapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 whitespace-nowrap">
                    {user?.username || t('common.user')}
                  <div className="absolute top-1/2 -left-1 transform -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                </div>
              )}
            </Link>
            
            {/* Logout button for collapsed state */}
            {sidebarCollapsed && (
              <motion.button
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.95 }}
                onClick={logout}
                className={`w-full flex items-center justify-center px-2 py-2 mt-2 rounded-lg transition-colors ${isLightTheme 
                  ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' 
                  : 'text-white/60 hover:text-white hover:bg-white/5'
                } relative group`}
                aria-label={t('sidebar.logout')}
                title={t('sidebar.logout')}
              >
                <LogOut className="h-4 w-4" />
                {/* Tooltip for logout button */}
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 whitespace-nowrap">
                  {t('sidebar.logout')}
                  <div className="absolute top-1/2 -left-1 transform -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                </div>
              </motion.button>
            )}
            
          </motion.div>
          {/* Close content area with border */}
          </div>
        </div>
      </motion.aside>

      {/* Mobile sidebar overlay */}
      {isMobile && sidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => toggleSidebar(false)}
        ></motion.div>
      )}

      {/* Mobile sidebar */}
      {isMobile && (
        <motion.aside
          initial={{ x: isRTL ? '100%' : '-100%' }}
          animate={{ x: sidebarOpen ? 0 : isRTL ? '100%' : '-100%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className={`lg:hidden fixed inset-y-0 ${isRTL ? 'right-0' : 'left-0'} z-50 w-64 shadow-lg ${isLightTheme 
            ? `bg-white/95 ${isRTL ? 'border-l' : 'border-r'} border-gray-200` 
            : `bg-white/5 backdrop-blur-xl ${isRTL ? 'border-l' : 'border-r'} border-white/10`
          }`}
        >
          <div className="h-full flex flex-col">
            <div className="flex items-start justify-between gap-3 px-4 py-5">
              <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
                <BrandProtectedImg
                  src={`${getLogoPath(isLightTheme)}${BRAND_QUERY}`}
                  alt={t('sidebar.software_name')}
                  className="h-8 w-auto max-w-[9.75rem] object-contain object-left"
                  onError={(e) => {
                    e.target.src = `${getLogoPath(!isLightTheme)}${BRAND_QUERY}`;
                  }}
                />
                <p
                  className={`text-left text-xs leading-snug ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`}
                >
                  {t('sidebar.software_tagline')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => toggleSidebar(false)}
                className={`shrink-0 self-start p-2 rounded-xl transition-colors ${
                  isLightTheme
                    ? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
                aria-label={t('common.close')}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav dir={isRTL ? 'rtl' : 'ltr'} className={`mt-8 flex flex-1 flex-col min-h-0 px-2 ${isRTL ? 'text-right' : ''}`}>
              <div className="min-h-0 flex-1 overflow-y-auto dark-scrollbar">
                <div className="space-y-2 pb-2">
                  {filteredModules
                  .map((module) => {
                  const isExpanded = expandedModules[module.key];
                  const showFlatSubsectionsMobile = isSingleModuleNav;
                  const showSubsectionsMobile = showFlatSubsectionsMobile || isExpanded;
                  const isAnySubsectionActive = module.subsections.some(sub => 
                    location.pathname === sub.href || location.pathname.startsWith(sub.href + '/')
                  );
                  const showMainGammeHeader = shouldShowMainGammeSectionHeader(
                    module.key,
                    filteredModuleKeys,
                  );
                  const showGammeSeparator = shouldShowMainGammeSeparator(
                    module.key,
                    filteredModuleKeys,
                  );
                  const showIndustrySectorSeparator = shouldShowIndustrySectorSeparator(
                    module.key,
                    filteredModuleKeys,
                  );
                  
                  return (
                    <React.Fragment key={module.key}>
                      <SidebarTierSectionHeader
                        show={showMainGammeHeader}
                        label={t('sidebar.gamme_main')}
                        isLightTheme={isLightTheme}
                        collapsed={false}
                        showLine={false}
                      />
                      <SidebarTierSectionHeader
                        show={showGammeSeparator}
                        label={t('sidebar.gamme_industry')}
                        isLightTheme={isLightTheme}
                        collapsed={false}
                      />
                      <SidebarTierSectionHeader
                        show={showIndustrySectorSeparator}
                        label={t('sidebar.gamme_sector')}
                        isLightTheme={isLightTheme}
                        collapsed={false}
                      />
                    <div className="space-y-1">
                      {/* Module Header (Collapsible) — hidden when exactly one module */}
                      {!isSingleModuleNav && (
                      <button
                        onClick={() => toggleModule(module.key)}
                        className={`w-full group flex items-center justify-between px-3 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${isLightTheme 
                          ? (isAnySubsectionActive
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-gray-900 hover:bg-gray-100')
                          : (isAnySubsectionActive
                            ? 'bg-white/10 text-white'
                            : 'text-white/80 hover:bg-white/5 hover:text-white')
                        }`}
                      >
                        <div className="flex items-center min-w-0 flex-1">
                          <module.icon
                            className="mr-3 flex-shrink-0 h-5 w-5"
                            aria-hidden="true"
                          />
                          <span className="truncate">{getModuleLabel(module, t)}</span>
                        </div>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </button>
                      )}

                      {/* Module Subsections (Collapsible) */}
                      {showSubsectionsMobile && (
                        <div className={showFlatSubsectionsMobile ? 'space-y-1' : 'ml-6 space-y-1'}>
                          {module.subsections.map((subsection) => {
                            const isCurrent = location.pathname === subsection.href || 
                                            location.pathname.startsWith(subsection.href + '/');
                            const flatM = showFlatSubsectionsMobile;
                            const mobileLinkClass = flatM
                              ? `group flex items-center px-2 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${isLightTheme
                                ? (isCurrent
                                  ? 'bg-blue-600 text-white shadow-sm'
                                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900')
                                : (isCurrent
                                  ? 'bg-white/10 text-white'
                                  : 'text-white/80 hover:bg-white/5 hover:text-white')
                              }`
                              : `group flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 ${isLightTheme
                                ? (isCurrent
                                  ? 'bg-blue-600 text-white shadow-sm'
                                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900')
                                : (isCurrent
                                  ? 'bg-white/10 text-white'
                                  : 'text-white/60 hover:bg-white/5 hover:text-white')
                              }`;
                            const mobileIconClass = flatM
                              ? `flex-shrink-0 h-5 w-5 me-3${isCurrent && isLightTheme ? ' text-white' : ''}`
                              : `flex-shrink-0 h-4 w-4 me-3 ${isCurrent ? (isLightTheme ? 'text-white' : 'text-blue-500') : ''}`;
                            
                            return (
                              <Link
                                key={subsection.href}
                                to={subsection.href}
                                onClick={closeSidebar}
                                className={mobileLinkClass}
                              >
                                <subsection.icon
                                  className={mobileIconClass}
                                  aria-hidden="true"
                                />
                                {getSubsectionLabel(subsection, t)}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    </React.Fragment>
                  );
                })}
                </div>
              </div>

              {/* Paramètres — aligné web : au-dessus du profil, toujours visible sans scroller toute la liste */}
              <div className={`shrink-0 border-t pt-2 pb-1 ${isLightTheme ? 'border-gray-200' : 'border-white/10'}`}>
                <Link
                  to="/settings"
                  onClick={closeSidebar}
                  className={`group flex items-center px-2 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${isLightTheme
                    ? (location.pathname === '/settings'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900')
                    : (location.pathname === '/settings'
                      ? 'bg-white/10 text-white'
                      : 'text-white/80 hover:bg-white/5 hover:text-white')
                  }`}
                >
                  <Settings className="me-3 flex-shrink-0 h-5 w-5" aria-hidden="true" />
                  <span>{t('sidebar.settings')}</span>
                </Link>
              </div>
            </nav>

            {/* User profile section on mobile */}
            <div className={`px-3 py-4 border-t ${isLightTheme ? 'border-gray-200' : 'border-white/10'}`}>
              <Link 
                to="/profile"
                className={`flex items-center px-2 py-2 mt-1 group rounded-lg transition-colors ${isLightTheme 
                  ? 'hover:bg-gray-100' 
                  : 'hover:bg-white/5'
                }`}
              >
                <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mr-3">
                  <span className="text-sm font-medium text-white">{user?.username?.charAt(0)?.toUpperCase() || 'U'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${isLightTheme ? 'text-gray-900' : 'text-white'}`}>
                    {user?.username || t('common.user')}
                  </p>
                  <p className={`text-xs truncate ${isLightTheme ? 'text-gray-600' : 'text-white/60'}`}>
                    {user?.role || t('sidebar.user_role')}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    logout();
                  }}
                  className={`ml-auto p-1 transition-colors ${isLightTheme 
                    ? 'text-gray-600 hover:text-gray-900' 
                    : 'text-white/60 hover:text-white'
                  }`}
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </Link>
              
            </div>
        </div>
        </motion.aside>
      )}
    </>
  );
};

export default Sidebar;

